/** @jsxImportSource @emotion/react */
import { useState, useEffect, useCallback, ReactElement, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { StateShape as ReduxStateShape } from '../../reducers';

export const INITIAL_RIGHT_SIDEBAR_WIDTH = 280;
export const MAX_RIGHT_SIDEBAR_WIDTH = 700;

interface RightSideBarProps {
    children?: ((ReactElement<any, any> & ReactNode) & ReactNode & JSX.Element) | null;
    setRightSidebarWidth?: (width: number) => void;
    hide?: boolean;
    withAdditionalWidth?: boolean;
}

const RightSideBar = (props: RightSideBarProps) => {
    const { children, setRightSidebarWidth, hide, withAdditionalWidth } = props;
    const [isResizing, setIsResizing] = useState(false);
    const [sidebarWidth, setSidebarWidth] = useState<any>(hide ? 0 : INITIAL_RIGHT_SIDEBAR_WIDTH);

    const startResizing = useCallback((_mouseDownEvent: any) => {
        setIsResizing(true);
    }, []);

    const stopResizing = useCallback(() => {
        setIsResizing(false);
    }, []);

    const { isShowResourcePicker, isShowAnnotationPicker, isShowDataPicker, pageResourcesAction } =
        useSelector((state: ReduxStateShape) => ({
            ...state.resources,
            ...state.templateEditor,
        }));

    useEffect(() => {
        const appElement = document.querySelector<HTMLElement>('.app');
        const appCanvasElement = document.getElementById('app-canvas');
        
        if (
            isShowResourcePicker ||
            isShowAnnotationPicker ||
            isShowDataPicker ||
            !!pageResourcesAction
        ) {
            // Use the current sidebar width from state instead of reading from DOM
            // to avoid cumulative width calculation issues
            const totalSidebarWidth = sidebarWidth + 275;
            console.log(
                'isShowResourcePicker',
                !!pageResourcesAction,
                `calc(100% - ${totalSidebarWidth}px)`
            );
            // push app content when new sidebar added
            appElement &&
                appElement!.style.setProperty('width', `calc(100% - ${totalSidebarWidth}px)`);
            appCanvasElement &&
                appCanvasElement!.style.setProperty('width', `calc(100% - ${totalSidebarWidth}px)`);
        } else {
            // Reset app content width when all tabs are closed
            // Force removal to ensure no other code overrides this
            if (appElement) {
                appElement.style.removeProperty('width');
                // Also check and remove any calc-based width styles that might have been set
                if (appElement.style.width && appElement.style.width.includes('calc')) {
                    appElement.style.width = '';
                }
            }
            if (appCanvasElement) {
                appCanvasElement.style.removeProperty('width');
                // Also check and remove any calc-based width styles that might have been set
                if (appCanvasElement.style.width && appCanvasElement.style.width.includes('calc')) {
                    appCanvasElement.style.width = '';
                }
            }
        }
    }, [isShowResourcePicker, isShowAnnotationPicker, isShowDataPicker, pageResourcesAction, sidebarWidth]);

    useEffect(() => {
        if (!hide) {
            const baseWidth = withAdditionalWidth
                ? INITIAL_RIGHT_SIDEBAR_WIDTH + 275
                : INITIAL_RIGHT_SIDEBAR_WIDTH;
            const toolelement = document.querySelector<HTMLElement>('.right-sidebar-tool');
            toolelement && toolelement!.style.setProperty('width', `${baseWidth - 10}px`);
            setSidebarWidth(baseWidth);
            setRightSidebarWidth && setRightSidebarWidth(baseWidth);
        } else {
            const toolelement = document.querySelector<HTMLElement>('.right-sidebar-tool');
            toolelement && toolelement!.style.setProperty('width', `0px`);
            setSidebarWidth(0);
            setRightSidebarWidth && setRightSidebarWidth(0);
        }
    }, [hide, withAdditionalWidth]);

    // Resize function for changing the width of sidebar
    const resize = useCallback(
        (mouseMoveEvent: any) => {
            if (isResizing) {
                const elementLeftWidth =
                    document.getElementById('right-side-bar-main')?.getBoundingClientRect().left ||
                    0;
                const elementWidth =
                    document.getElementById('right-side-bar-main')?.getBoundingClientRect().width ||
                    0;
                const totalElement = elementLeftWidth + elementWidth;
                const currentWidth = totalElement - mouseMoveEvent.clientX;
                document
                    .getElementById('right-side-bar-main')
                    ?.style.setProperty('max-width', '700px');

                // conditions to make sure doesn't resizing doesn't pass max width and min width
                if (currentWidth < MAX_RIGHT_SIDEBAR_WIDTH && currentWidth > 280) {
                    const toolelement = document.querySelector<HTMLElement>('.right-sidebar-tool');
                    toolelement &&
                        toolelement!.style.setProperty('width', `${currentWidth - 10}px`);
                    setSidebarWidth(currentWidth);
                    setRightSidebarWidth && setRightSidebarWidth(currentWidth);
                } else if (currentWidth >= MAX_RIGHT_SIDEBAR_WIDTH) {
                    setSidebarWidth(MAX_RIGHT_SIDEBAR_WIDTH);
                    setRightSidebarWidth && setRightSidebarWidth(MAX_RIGHT_SIDEBAR_WIDTH);
                    const toolelement = document.querySelector<HTMLElement>('.right-sidebar-tool');
                    toolelement &&
                        toolelement!.style.setProperty(
                            'width',
                            `${MAX_RIGHT_SIDEBAR_WIDTH - 10}px`
                        );
                } else if (currentWidth <= 280) {
                    setSidebarWidth(280);
                    setRightSidebarWidth && setRightSidebarWidth(280);
                    const toolelement = document.querySelector<HTMLElement>('.right-sidebar-tool');
                    toolelement && toolelement!.style.setProperty('width', `${280 - 10}px`);
                }
            }
        },
        [isResizing]
    );

    useEffect(() => {
        window.addEventListener('mousemove', resize);
        window.addEventListener('mouseup', stopResizing);
        return () => {
            window.removeEventListener('mousemove', resize);
            window.removeEventListener('mouseup', stopResizing);
        };
    }, [resize, stopResizing]);

    return (
        <>
            <div
                data-testid="sidebar"
                className="right-side-bar"
                id="right-side-bar-main"
                style={{ width: sidebarWidth }}
            >
                <div
                    data-testid="resizer"
                    className="sidebar-resizer"
                    onMouseDown={startResizing}
                ></div>
                <div className="drawer-container">{children}</div>
            </div>
        </>
    );
};

export default RightSideBar;
